import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Plus, Star, Pencil, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, Field, FormRow, EmptyState, ErrorState, TableSkeleton, fmtDate, plural } from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, useStableCallback, IconButton, toDay } from './hrKit';

// Ratings are an assessment, so only the two ends carry a colour.
const RATINGS = [
  { value: 'outstanding', label: 'Outstanding', cls: 'badge-gray' },
  { value: 'exceeds_expectations', label: 'Exceeds expectations', cls: 'badge-gray' },
  { value: 'satisfactory', label: 'Satisfactory', cls: 'badge-gray' },
  { value: 'needs_improvement', label: 'Needs improvement', cls: 'badge-yellow' },
  { value: 'unsatisfactory', label: 'Unsatisfactory', cls: 'badge-red' },
];
const rating = (v) => RATINGS.find((r) => r.value === v) || RATINGS[2];
const STATUS = {
  draft: { label: 'Draft', cls: 'badge-gray' },
  completed: { label: 'Completed', cls: 'badge-green' },
  acknowledged: { label: 'Acknowledged', cls: 'badge-blue' },
};
const dateOf = (v) => (v ? String(v).slice(0, 10) : '');
const showDate = (v) => (v ? fmtDate(dateOf(v)) : '—');
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function AppraisalDialog({ open, staffId, appraisal, onSaved, onClose }) {
  const isEdit = !!appraisal;
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(appraisal ? {
      appraisal_date: dateOf(appraisal.appraisal_date), period_from: dateOf(appraisal.period_from), period_to: dateOf(appraisal.period_to),
      overall_rating: appraisal.overall_rating || 'satisfactory', strengths: appraisal.strengths || '',
      areas_for_improvement: appraisal.areas_for_improvement || '', goals_set: appraisal.goals_set || '',
      comments: appraisal.comments || '', status: appraisal.status || 'draft',
    } : { appraisal_date: todayKey(), period_from: '', period_to: '', overall_rating: 'satisfactory', strengths: '', areas_for_improvement: '', goals_set: '', comments: '', status: 'draft' });
  }, [open, appraisal]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    if (!form.appraisal_date) { setErrors({ appraisal_date: 'Choose the appraisal date.' }); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/staff/${staffId}/appraisals/${appraisal.id}`, form);
      else await api.post(`/staff/${staffId}/appraisals`, form);
      toast.success(isEdit ? 'Appraisal updated' : 'Appraisal added');
      setSaving(false); onSaved(); onClose();
    } catch (err) { setErrors({ form: err.response?.data?.message || "The appraisal wasn't saved. Try again." }); setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={close} size="lg" title={isEdit ? 'Edit appraisal' : 'Add appraisal'}
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="appraisal-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add appraisal')}</button>
        </>
      )}>
      <form id="appraisal-form" onSubmit={save} className="space-y-4">
        <FormRow cols={3}>
          <Field label="Appraisal date" required error={errors.appraisal_date}>
            {(id) => <input id={id} type="date" className="input-field" value={form.appraisal_date || ''} onChange={(e) => set('appraisal_date', e.target.value)} />}
          </Field>
          <Field label="Period from">
            {(id) => <input id={id} type="date" className="input-field" value={form.period_from || ''} onChange={(e) => set('period_from', e.target.value)} />}
          </Field>
          <Field label="Period to">
            {(id) => <input id={id} type="date" className="input-field" value={form.period_to || ''} onChange={(e) => set('period_to', e.target.value)} />}
          </Field>
        </FormRow>
        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-slate-700">Overall rating</legend>
          <div className="flex flex-wrap gap-2">
            {RATINGS.map((r) => {
              const on = form.overall_rating === r.value;
              return (
                <button key={r.value} type="button" aria-pressed={on} onClick={() => set('overall_rating', r.value)}
                  className={clsx('h-9 rounded-lg border px-3 text-[13px] font-medium transition-colors duration-150',
                    on ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300')}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </fieldset>
        <FormRow>
          <Field label="Status">
            {(id) => (
              <select id={id} className="input-field" value={form.status || 'draft'} onChange={(e) => set('status', e.target.value)}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            )}
          </Field>
        </FormRow>
        <Field label="Strengths">{(id) => <textarea id={id} className="input-field" rows={3} value={form.strengths || ''} onChange={(e) => set('strengths', e.target.value)} />}</Field>
        <Field label="Areas for improvement">{(id) => <textarea id={id} className="input-field" rows={3} value={form.areas_for_improvement || ''} onChange={(e) => set('areas_for_improvement', e.target.value)} />}</Field>
        <Field label="Goals set">{(id) => <textarea id={id} className="input-field" rows={3} value={form.goals_set || ''} onChange={(e) => set('goals_set', e.target.value)} />}</Field>
        <Field label="Other comments">{(id) => <textarea id={id} className="input-field" rows={2} value={form.comments || ''} onChange={(e) => set('comments', e.target.value)} />}</Field>
      </form>
    </Dialog>
  );
}

export default function StaffAppraisal() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get(`/staff/${id}/appraisals`)
      .then((r) => setAppraisals(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load the appraisals. Try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const closeDialog = useStableCallback(() => { setDialogOpen(false); setEditing(null); });

  async function deleteAppraisal(a) {
    if (!await confirm(`The appraisal dated ${showDate(a.appraisal_date)} will be deleted. This can't be undone.`, { title: 'Delete appraisal?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/${id}/appraisals/${a.id}`);
      toast.success('Appraisal deleted');
      setAppraisals((list) => list.filter((x) => x.id !== a.id));
    } catch (e) { toast.error(e.response?.data?.message || "The appraisal wasn't deleted. Try again."); }
  }

  const sorted = [...appraisals].sort((a, b) => (toDay(b.appraisal_date || b.created_at) || 0) - (toDay(a.appraisal_date || a.created_at) || 0));
  const openNew = () => { setEditing(null); setDialogOpen(true); };

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[13px] text-slate-600">{loading ? 'Loading…' : plural(appraisals.length, 'appraisal')}</p>
          <button type="button" onClick={openNew} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />Add appraisal</button>
        </div>
        {loading ? <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={3} cols={3} /></div>
          : loadError ? <div className="rounded-xl border border-slate-200 bg-white"><ErrorState message={loadError} onRetry={load} /></div>
            : sorted.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white">
                <EmptyState icon={Star} title="No appraisals yet" hint="Record performance reviews, the goals you agreed and how they went."
                  action={<button type="button" onClick={openNew} className="btn-secondary btn-sm"><Plus size={14} aria-hidden="true" />Add first appraisal</button>} />
              </div>
            ) : (
              <ul className="space-y-3">
                {sorted.map((a) => {
                  const r = rating(a.overall_rating);
                  const st = STATUS[a.status] || STATUS.draft;
                  return (
                    <li key={a.id} className="rounded-xl border border-slate-200 bg-white">
                      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900">Appraisal, {showDate(a.appraisal_date)}</p>
                          {(a.period_from || a.period_to) && <p className="text-[12.5px] text-slate-500">Covers {showDate(a.period_from)} – {showDate(a.period_to)}</p>}
                        </div>
                        <span className={r.cls}>{r.label}</span>
                        <span className={st.cls}>{st.label}</span>
                        <IconButton label="Edit appraisal" icon={Pencil} onClick={() => { setEditing(a); setDialogOpen(true); }} />
                        <IconButton label="Delete appraisal" icon={Trash2} danger onClick={() => deleteAppraisal(a)} />
                      </div>
                      <div className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-3">
                        {[['Strengths', a.strengths], ['Areas for improvement', a.areas_for_improvement], ['Goals set', a.goals_set]].map(([label, val]) => (
                          <div key={label}>
                            <h4 className="text-[12px] font-semibold text-slate-500">{label}</h4>
                            <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{val || <span className="text-slate-500">Not recorded.</span>}</p>
                          </div>
                        ))}
                      </div>
                      {a.comments && (
                        <div className="border-t border-slate-100 px-5 py-3">
                          <h4 className="text-[12px] font-semibold text-slate-500">Comments</h4>
                          <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-700">{a.comments}</p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
      </div>
      <AppraisalDialog open={dialogOpen} staffId={id} appraisal={editing} onSaved={load} onClose={closeDialog} />
    </div>
  );
}
